import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";
import {
  BetfairStreamApiEvent,
  PriceMatchingData,
  TranslatedBetfairOdds,
} from "../../types/betfair/price-matching/price-matching-data";
import { Ball } from "../../types/entities/ball";
import { CricketEvent } from "../../types/entities/cricket-event";
import { EventType } from "../../types/enums/event-type";
import { format, formatToDp } from "../simulator-page/simulator-utils";

interface Props {
  eventData: PriceMatchingData;
}

export default function PriceMatchingDataTable({
  eventData,
}: Readonly<Props>): React.JSX.Element {
  eventData.firstInningsBetfairRunsLineOdds.sort(
    (a: TranslatedBetfairOdds, b: TranslatedBetfairOdds) => a.time - b.time
  );
  const cricinfoBalls: Ball[] = eventData.eventInfo.events
    .filter(
      (event: CricketEvent) =>
        event.eventType === EventType.BOWL && event.innings === 1
    )
    .map((b) => b as Ball);

  const getNearestPrice = (time: number) => {
    let price = null;
    for (let odd of eventData.firstInningsBetfairRunsLineOdds) {
      if (odd.time > time) {
        break;
      }
      price = odd.price;
    }
    return price;
  };

  let attempt = 0;
  let over = null;
  const streamEvents = eventData.streamApiEvents.map(
    (apiEvent: BetfairStreamApiEvent) => {
      if (apiEvent.over === over) {
        attempt++;
      } else {
        attempt = 0;
      }

      over = apiEvent.over;
      const nearestPrice = getNearestPrice(apiEvent.time);

      return { ...apiEvent, attempt, nearestPrice };
    }
  );

  const getBallDescription = (ball) => {
    const runsString = ball.runs + " runs ";
    const noBallString = ball.noBall ? "NO BALL" : "";
    const wideString = ball.wide ? "WIDE" : "";
    const legByeString = ball.legBye ? "LEG BYE" : "";
    const byeString = ball.bye ? "BYE" : "";
    return ball.dismissal
      ? "OUT"
      : runsString + noBallString + wideString + legByeString + byeString;
  };

  return (
    <div>
      <TableContainer>
        <Table>
          <TableHead>
            <TableCell>Stream API Over</TableCell>
            <TableCell>Stream API Attempt</TableCell>
            <TableCell>Stream API Price</TableCell>
            <TableCell>Cricinfo Over</TableCell>
            <TableCell>Cricinfo Attempt</TableCell>
            <TableCell>Cricinfo Event</TableCell>
            <TableCell>Matched Price</TableCell>
            <TableCell>Time Difference (s)</TableCell>
            <TableCell>Price Difference</TableCell>
            <TableCell>Market Delta</TableCell>
          </TableHead>
          <TableBody>
            {cricinfoBalls.map((ball: Ball, index: number) => {
              const betfairHistoricPrice = eventData.matchedPrices.get(
                ball.bowlId.value
              );
              const previousBetfairHistoricPrice =
                eventData.matchedPrices.get(
                  cricinfoBalls[index - 1]?.bowlId.value
                ) || null;

              const streamApiEvent = streamEvents.find(
                (se) => se.over === ball.over && se.attempt === ball.ballAttempt
              );
              const timeDifference =
                betfairHistoricPrice && streamApiEvent
                  ? betfairHistoricPrice.betfairMarketTime - streamApiEvent.time
                  : 0;
              const priceDifference =
                betfairHistoricPrice && streamApiEvent
                  ? betfairHistoricPrice.betfairPrice -
                    streamApiEvent.nearestPrice
                  : 0;
              const delta =
                betfairHistoricPrice && previousBetfairHistoricPrice
                  ? betfairHistoricPrice?.betfairPrice -
                    previousBetfairHistoricPrice?.betfairPrice
                  : 0;
              return (
                <TableRow key={ball.bowlId.value}>
                  <TableCell>{streamApiEvent?.over}</TableCell>
                  <TableCell>{streamApiEvent?.attempt}</TableCell>
                  <TableCell>{format(streamApiEvent?.nearestPrice)}</TableCell>
                  <TableCell>{ball.over}</TableCell>
                  <TableCell>{ball.ballAttempt}</TableCell>
                  <TableCell>{getBallDescription(ball)}</TableCell>
                  <TableCell>
                    {format(betfairHistoricPrice?.betfairPrice)}
                  </TableCell>
                  <TableCell>
                    {isNaN(timeDifference)
                      ? ""
                      : formatToDp(timeDifference / 1000, 0)}
                  </TableCell>
                  <TableCell>
                    {isNaN(priceDifference)
                      ? ""
                      : formatToDp(priceDifference, 2)}
                  </TableCell>
                  <TableCell>{formatToDp(delta, 2)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
